import { useState } from 'react';
import { useToast } from './useToast';
import { downloadFile, DownloadFileOptions } from '../utils/downloadUtils';

interface UseDownloadOptions {
  onSuccess?: (filename: string) => void;
  onError?: (error: Error) => void;
}

interface UseDownloadReturn {
  isDownloading: boolean;
  downloadProgress: number;
  download: (options: DownloadFileOptions) => Promise<void>;
  downloadPDF: (endpoint: string, filename: string) => Promise<void>;
  downloadExcel: (endpoint: string, filename: string) => Promise<void>;
  downloadCSV: (endpoint: string, filename: string) => Promise<void>;
}

/**
 * Custom hook for handling file downloads with progress tracking and toast notifications
 */
export const useDownload = (options: UseDownloadOptions = {}): UseDownloadReturn => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const toast = useToast();
  const { onSuccess, onError } = options;

  const download = async (downloadOptions: DownloadFileOptions): Promise<void> => {
    try {
      setIsDownloading(true);
      setDownloadProgress(0);

      await downloadFile({
        ...downloadOptions,
        onProgress: (progress) => {
          setDownloadProgress(progress);
          downloadOptions.onProgress?.(progress);
        },
      });
      // toast.success('Download completed!', {
      //   description: `${downloadOptions.filename} has been saved to your downloads folder.`,
      // });
      onSuccess?.(downloadOptions.filename);
    } catch (error: any) {
      console.error('Download failed:', error);

      let errorMessage = 'Failed to download file. Please try again.';

      if (error.message.includes('File is empty')) {
        errorMessage = 'File is empty or corrupted. Please contact support.';
      } else if (error.message.includes('404')) {
        errorMessage = 'File not found. Please contact support.';
      } else if (error.message.includes('403')) {
        errorMessage = 'Access denied. Please ensure you are logged in.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error('Download Failed', {
        description: errorMessage,
      });

      onError?.(error);
      throw error;
    } finally {
      setIsDownloading(false);
      setDownloadProgress(0);
    }
  };

  const downloadPDF = async (endpoint: string, filename: string): Promise<void> => {
    return download({
      endpoint,
      filename,
      mimeType: 'application/pdf',
    });
  };

  const downloadExcel = async (endpoint: string, filename: string): Promise<void> => {
    return download({
      endpoint,
      filename,
      mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
  };

  const downloadCSV = async (endpoint: string, filename: string): Promise<void> => {
    return download({
      endpoint,
      filename,
      mimeType: 'text/csv',
    });
  };

  return {
    isDownloading,
    downloadProgress,
    download,
    downloadPDF,
    downloadExcel,
    downloadCSV,
  };
};